import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, Subject } from 'rxjs';
import { retry, takeUntil, timeout } from 'rxjs/operators';

import { AuthService } from '@app/modules/admin/auth/services/auth.service';
import { environment } from '@utility/app.util';
import { NotificationService } from '@utility/services/notification.service';

interface HubSpotConversationsApi {
  widget: {
    load(): void;
    remove(): void;
  };
  clear(options?: { resetWidget?: boolean }): void;
  on(event: 'userInteractedWithWidget', callback: () => void): void;
  off?(event: 'userInteractedWithWidget', callback: () => void): void;
}

interface HubSpotTokenResponse {
  data: {
    token: string;
  };
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    HubSpotConversations?: HubSpotConversationsApi;
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: {
      loadImmediately: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Cypress?: unknown;
  }
}

const HUBSPOT_SCRIPT_ID = 'hs-script-loader';
const HUBSPOT_SCRIPT_HOST = 'https://js.hs-scripts.com';
const HUBSPOT_REQUEST_TIMEOUT_MS = 10 * 1000;
const HUBSPOT_WIDGET_READY_TIMEOUT_MS = 10 * 1000;
const VISITOR_TOKEN_REFRESH_AFTER_MS = 11 * 60 * 60 * 1000;
const SUPPORT_UNAVAILABLE_MESSAGE_KEY = 'Support chat is temporarily unavailable. Please try again later.';

@Injectable({
  providedIn: 'root',
})
export class HubSpotChatService {
  private initialization?: Promise<void>;
  private visitorToken?: string;
  private visitorTokenRefreshAt: number = 0;
  private ready: boolean = false;
  private blocked: boolean = false;
  private contextSyncStarted: boolean = false;
  private lifecycleVersion: number = 0;
  private readonly requestCancellation: Subject<void> = new Subject<void>();
  private cancelPendingWidgetLoad?: () => void;
  private visitorTokenRefreshTimer?: number;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private translocoService: TranslocoService,
  ) {}

  public initialize(): Promise<void> {
    if (
      window.Cypress ||
      !this.isHubSpotConfigured() ||
      this.blocked ||
      (this.authService.isCookieConsentEnabled && !this.authService.isCookieConsentAccepted)
    ) {
      return Promise.resolve();
    }

    if (this.ready && Date.now() < this.visitorTokenRefreshAt) {
      return Promise.resolve();
    }

    if (!this.initialization) {
      const lifecycleVersion = this.lifecycleVersion;
      this.initialization = this.initializeIdentifiedWidget(lifecycleVersion)
        .catch((): void => {
          if (lifecycleVersion === this.lifecycleVersion) {
            this.blockChat();
          }
        })
        .finally((): void => {
          if (lifecycleVersion === this.lifecycleVersion) {
            this.initialization = undefined;
          }
        });
    }

    return this.initialization;
  }

  public remove(): void {
    this.lifecycleVersion += 1;
    this.requestCancellation.next();
    this.cancelPendingWidgetLoad?.();
    this.cancelVisitorTokenRefresh();
    const conversations = window.HubSpotConversations;
    conversations?.off?.('userInteractedWithWidget', this.onUserInteractedWithWidget);

    if (conversations) {
      window.hsConversationsSettings = { loadImmediately: false };
      conversations.clear({ resetWidget: true });
      conversations.widget.remove();
    }

    this.document.getElementById(HUBSPOT_SCRIPT_ID)?.remove();
    delete window.hsConversationsSettings;

    this.visitorToken = undefined;
    this.visitorTokenRefreshAt = 0;
    this.ready = false;
    this.blocked = false;
    this.contextSyncStarted = false;
    this.initialization = undefined;
  }

  private readonly onUserInteractedWithWidget = (): void => {
    if (this.contextSyncStarted || this.blocked || !this.ready) {
      return;
    }

    this.contextSyncStarted = true;
    void this.syncSupportContext(this.lifecycleVersion);
  };

  private async initializeIdentifiedWidget(lifecycleVersion: number): Promise<void> {
    const hubSpotScriptUrl = this.hubSpotScriptUrl();
    const email = this.authService.currentUser.value?.email;

    if (!email) {
      throw new Error('Authenticated Admin identity is not available.');
    }

    const response = await firstValueFrom(
      this.http
        .post<HubSpotTokenResponse>(`${environment.apiUrl}/v2/hubspot/chat/token/create`, {})
        .pipe(
          timeout(HUBSPOT_REQUEST_TIMEOUT_MS),
          retry({ count: 1, delay: 500 }),
          takeUntil(this.requestCancellation),
        ),
    );

    if (lifecycleVersion !== this.lifecycleVersion) {
      return;
    }

    if (!response?.data?.token) {
      throw new Error('HubSpot visitor token was not returned.');
    }

    this.visitorToken = response.data.token;
    this.visitorTokenRefreshAt = Date.now() + VISITOR_TOKEN_REFRESH_AFTER_MS;
    window.hsConversationsSettings = {
      loadImmediately: false,
      identificationEmail: email,
      identificationToken: this.visitorToken,
    };

    await this.loadWidget(lifecycleVersion, hubSpotScriptUrl);

    if (lifecycleVersion === this.lifecycleVersion) {
      this.ready = true;
      this.scheduleVisitorTokenRefresh(lifecycleVersion);
    }
  }

  private scheduleVisitorTokenRefresh(lifecycleVersion: number): void {
    this.cancelVisitorTokenRefresh();
    const refreshDelay = Math.max(0, this.visitorTokenRefreshAt - Date.now());

    this.visitorTokenRefreshTimer = window.setTimeout((): void => {
      this.visitorTokenRefreshTimer = undefined;

      if (lifecycleVersion !== this.lifecycleVersion || this.blocked || !this.ready) {
        return;
      }

      this.visitorTokenRefreshAt = 0;
      void this.initialize();
    }, refreshDelay);
  }

  private cancelVisitorTokenRefresh(): void {
    if (this.visitorTokenRefreshTimer === undefined) {
      return;
    }

    window.clearTimeout(this.visitorTokenRefreshTimer);
    this.visitorTokenRefreshTimer = undefined;
  }

  private loadWidget(lifecycleVersion: number, hubSpotScriptUrl: string): Promise<void> {
    return new Promise((resolve, reject): void => {
      let settled = false;
      const cleanup = (): void => {
        window.clearTimeout(readyTimeout);

        window.hsConversationsOnReady = window.hsConversationsOnReady?.filter(
          (callback): boolean => callback !== onReady,
        );

        if (this.cancelPendingWidgetLoad === cancel) {
          this.cancelPendingWidgetLoad = undefined;
        }
      };
      const settle = (callback: () => void): void => {
        if (settled) {
          return;
        }

        settled = true;
        cleanup();
        callback();
      };
      const cancel = (): void => settle(resolve);
      const onReady = (): void => {
        if (lifecycleVersion !== this.lifecycleVersion) {
          settle(resolve);

          return;
        }

        const conversations = window.HubSpotConversations;

        if (!conversations) {
          settle((): void => reject(new Error('HubSpot Conversations API did not initialize.')));

          return;
        }

        conversations.off?.('userInteractedWithWidget', this.onUserInteractedWithWidget);
        conversations.on('userInteractedWithWidget', this.onUserInteractedWithWidget);
        conversations.widget.load();
        settle(resolve);
      };
      const readyTimeout = window.setTimeout(
        (): void =>
          settle((): void => reject(new Error('HubSpot Conversations API did not initialize in time.'))),
        HUBSPOT_WIDGET_READY_TIMEOUT_MS,
      );

      this.cancelPendingWidgetLoad = cancel;

      if (window.HubSpotConversations) {
        onReady();

        return;
      }

      window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
      window.hsConversationsOnReady.push(onReady);

      const existingScript = this.document.getElementById(HUBSPOT_SCRIPT_ID) as HTMLScriptElement | null;
      if (existingScript) {
        return;
      }

      const script = this.document.createElement('script');
      script.id = HUBSPOT_SCRIPT_ID;
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      script.src = hubSpotScriptUrl;
      script.onerror = (): void => settle((): void => reject(new Error('HubSpot script failed to load.')));
      this.document.head.appendChild(script);
    });
  }

  private async syncSupportContext(lifecycleVersion: number): Promise<void> {
    try {
      await firstValueFrom(
        this.http
          .post(`${environment.apiUrl}/v2/hubspot/chat/context/sync`, {
            page: this.currentPagePath(),
          })
          .pipe(
            timeout(HUBSPOT_REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: 500 }),
            takeUntil(this.requestCancellation),
          ),
      );
    } catch {
      if (lifecycleVersion === this.lifecycleVersion) {
        this.blockChat();
      }
    }
  }

  private blockChat(): void {
    if (this.blocked) {
      return;
    }

    this.blocked = true;
    this.lifecycleVersion += 1;
    this.requestCancellation.next();
    this.cancelPendingWidgetLoad?.();
    this.cancelVisitorTokenRefresh();
    this.ready = false;
    this.visitorToken = undefined;
    this.visitorTokenRefreshAt = 0;
    window.HubSpotConversations?.off?.('userInteractedWithWidget', this.onUserInteractedWithWidget);
    window.HubSpotConversations?.widget.remove();
    this.document.getElementById(HUBSPOT_SCRIPT_ID)?.remove();
    window.hsConversationsSettings = { loadImmediately: false };
    this.initialization = undefined;
    this.notificationService.showError(this.translocoService.translate(SUPPORT_UNAVAILABLE_MESSAGE_KEY));
  }

  private currentPagePath(): string {
    const [path] = this.router.url.split(/[?#]/);

    return path || '/';
  }

  private isHubSpotConfigured(): boolean {
    const portalId = String(environment.hubSpotPortalId ?? '').trim();

    return portalId !== '' && !/^\$\{[^}]+\}$/.test(portalId);
  }

  private hubSpotScriptUrl(): string {
    const portalId = String(environment.hubSpotPortalId ?? '').trim();

    if (!/^\d+$/.test(portalId)) {
      throw new Error('HubSpot portal ID is not configured.');
    }

    return `${HUBSPOT_SCRIPT_HOST}/${portalId}.js`;
  }
}
